import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useHtClient } from '../providers/ht';

export interface OnboardingTask {
  id: number;
  created_at: string;
  created_by: string;
  title: string;
  description: string | null;
  due: string | null;
  sf_account: string | null;
}

export interface OnboardingTaskCreateInput {
  title: string;
  description?: string;
  due?: string;
  sf_account?: string;
}

export interface OnboardingTaskUpdateInput {
  title?: string;
  description?: string;
  due?: string;
  sf_account?: string;
}

// Get all tasks with optional filters
export const useOnboardingTasks = (created_by?: string, sf_account?: string) => {
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ["onboardingTasks", { created_by, sf_account }],
    queryFn: async () => {
      console.log('📋 ONBOARDING TASKS: Fetching tasks', 
        created_by ? `for creator ${created_by}` : 'all',
        sf_account ? `and SF account ${sf_account}` : '');
      
      if (!client) {
        console.error('❌ ONBOARDING TASKS: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      // Build query parameters
      const params = new URLSearchParams();
      if (created_by !== undefined) params.append('created_by', created_by);
      if (sf_account !== undefined) params.append('sf_account', sf_account);
      
      const url = `/onboarding-tasks${params.toString() ? `?${params.toString()}` : ''}`;
      
      const startTime = performance.now();
      const response = await client.get(url);
      console.log(`⏱️ ONBOARDING TASKS: Fetch took ${Math.round(performance.now() - startTime)}ms`);
      
      console.log(`📦 ONBOARDING TASKS: Received ${response.data.length} tasks`);
      return response.data as OnboardingTask[];
    },
    staleTime: 60 * 1000, // 1 minute
  });
};

// Get specific task by ID
export const useOnboardingTask = (taskId: number) => {
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ["onboardingTask", taskId],
    queryFn: async () => {
      console.log(`🔍 ONBOARDING TASK: Fetching task ${taskId}`);
      
      if (!client) {
        console.error('❌ ONBOARDING TASK: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      const startTime = performance.now();
      const response = await client.get(`/onboarding-tasks/${taskId}`);
      console.log(`⏱️ ONBOARDING TASK: Fetch took ${Math.round(performance.now() - startTime)}ms`);
      
      console.log(`📦 ONBOARDING TASK: Received task data for ${response.data.title}`);
      return response.data as OnboardingTask;
    },
    staleTime: 60 * 1000, // 1 minute
    enabled: !!taskId,
  });
};

// Create new task
export const useCreateOnboardingTask = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (taskData: OnboardingTaskCreateInput) => {
      console.log('🆕 CREATE TASK MUTATION: Creating new task with data:', taskData);
      
      if (!client) {
        console.error('❌ CREATE TASK MUTATION: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      try {
        const startTime = performance.now();
        const response = await client.post("/onboarding-tasks", taskData);
        console.log(`⏱️ CREATE TASK MUTATION: Request took ${Math.round(performance.now() - startTime)}ms`);
        
        console.log('✅ CREATE TASK MUTATION: Successfully created task:', response.data);
        return response.data as OnboardingTask;
      } catch (error) {
        console.error('❌ CREATE TASK MUTATION: Request failed:', error);
        if (error instanceof Error) {
          console.error('❌ CREATE TASK MUTATION: Error details:', error.message);
        }
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('🔄 CREATE TASK MUTATION: Invalidating task queries');
      queryClient.invalidateQueries({ queryKey: ["onboardingTasks"] });
      
      // Also invalidate SF account-specific queries if applicable
      if (data.sf_account) {
        queryClient.invalidateQueries({ queryKey: ["onboardingTasks", { sf_account: data.sf_account }] });
        queryClient.invalidateQueries({ queryKey: ["tasksBySfAccount", data.sf_account] });
      }
    },
  });
};

// Update task
export const useUpdateOnboardingTask = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ taskId, taskData }: { taskId: number; taskData: OnboardingTaskUpdateInput }) => {
      console.log(`✏️ UPDATE TASK: Updating task ${taskId}`);
      
      if (!client) {
        console.error('❌ UPDATE TASK: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      const startTime = performance.now();
      const response = await client.put(`/onboarding-tasks/${taskId}`, taskData);
      console.log(`⏱️ UPDATE TASK: Request took ${Math.round(performance.now() - startTime)}ms`);
      
      console.log('✅ UPDATE TASK: Successfully updated task', response.data.id);
      return response.data as OnboardingTask;
    },
    onSuccess: (data, variables) => {
      console.log('🔄 UPDATE TASK: Invalidating task queries');
      
      // Invalidate general queries
      queryClient.invalidateQueries({ queryKey: ["onboardingTasks"] });
      queryClient.invalidateQueries({ queryKey: ["onboardingTask", variables.taskId] });
      
      // Also invalidate SF account-specific queries if applicable
      if (data.sf_account) {
        queryClient.invalidateQueries({ queryKey: ["onboardingTasks", { sf_account: data.sf_account }] });
        queryClient.invalidateQueries({ queryKey: ["tasksBySfAccount", data.sf_account] });
      }
    },
  });
};

// Delete task
export const useDeleteOnboardingTask = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (taskId: number) => {
      console.log(`🗑️ DELETE TASK: Deleting task ${taskId}`);
      
      if (!client) {
        console.error('❌ DELETE TASK: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      const startTime = performance.now();
      await client.delete(`/onboarding-tasks/${taskId}`);
      console.log(`⏱️ DELETE TASK: Request took ${Math.round(performance.now() - startTime)}ms`);
      
      console.log('✅ DELETE TASK: Successfully deleted task');
      return { id: taskId };
    },
    onSuccess: (_, taskId) => {
      console.log('🔄 DELETE TASK: Invalidating task queries');
      
      // Get the task data before it's removed from the cache
      const task = queryClient.getQueryData<OnboardingTask>(["onboardingTask", taskId]);
      
      // Invalidate the task-specific query
      queryClient.invalidateQueries({ queryKey: ["onboardingTask", taskId] });
      
      // Invalidate general tasks query
      queryClient.invalidateQueries({ queryKey: ["onboardingTasks"] });
      
      // Also invalidate SF account-specific queries if applicable
      if (task?.sf_account) {
        queryClient.invalidateQueries({ queryKey: ["onboardingTasks", { sf_account: task.sf_account }] });
        queryClient.invalidateQueries({ queryKey: ["tasksBySfAccount", task.sf_account] });
      }
    },
  });
};

// Get tasks by SF account
export const useTasksBySfAccount = (sfAccount: string) => {
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ["tasksBySfAccount", sfAccount],
    queryFn: async () => {
      console.log(`📋 TASKS BY SF ACCOUNT: Fetching tasks for SF account ${sfAccount}`);
      
      if (!client) {
        console.error('❌ TASKS BY SF ACCOUNT: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      const startTime = performance.now();
      const response = await client.get(`/onboarding-tasks/sf-account/${sfAccount}`);
      console.log(`⏱️ TASKS BY SF ACCOUNT: Fetch took ${Math.round(performance.now() - startTime)}ms`);
      
      console.log(`📦 TASKS BY SF ACCOUNT: Received ${response.data.length} tasks`);
      return response.data as OnboardingTask[];
    },
    staleTime: 60 * 1000, // 1 minute
  });
};

// Get tasks by creator
export const useTasksByCreator = (userId: string) => {
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ["tasksByCreator", userId],
    queryFn: async () => {
      console.log(`📋 TASKS BY CREATOR: Fetching tasks created by user ${userId}`);
      
      if (!client) {
        console.error('❌ TASKS BY CREATOR: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      const startTime = performance.now();
      const response = await client.get(`/onboarding-tasks/user/${userId}`);
      console.log(`⏱️ TASKS BY CREATOR: Fetch took ${Math.round(performance.now() - startTime)}ms`);
      
      console.log(`📦 TASKS BY CREATOR: Received ${response.data.length} tasks`);
      return response.data as OnboardingTask[];
    },
    staleTime: 60 * 1000, // 1 minute
  });
}; 